import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Modal, ProductCard, SearchBar, Typography } from '@growthaxl/web-foundation';
import { useShellStore } from '../../../shell/store/shellStore';
import headerData from '../data/globalHeaderData.json';
import { resolveLucideIcon } from '../utils/resolveLucideIcon';
import { useDynamicPlatformSolutions } from '../hooks/useDynamicPlatformSolutions';
import type { GlobalHeaderData, WaffleItem } from '../data/types';

const staticData = headerData as GlobalHeaderData;
const PLATFORM_GROUP_ID = 'platform-solutions';

export interface WaffleMenuProps {
  open: boolean;
  onClose: () => void;
  onSelectItem: (item: WaffleItem, applicationId: string) => void;
}

export function WaffleMenu({ open, onClose, onSelectItem }: WaffleMenuProps): React.ReactElement {
  const [query, setQuery] = useState('');
  const activeModule = useShellStore(state => state.activeModule);
  const activeSolution = useShellStore(state => state.activeSolution);
  const dynamicPlatformItems = useDynamicPlatformSolutions();

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const applications = useMemo(() => {
    if (!dynamicPlatformItems || dynamicPlatformItems.length === 0) {
      return staticData.applications;
    }

    return staticData.applications.map(app => {
      if (app.id !== PLATFORM_GROUP_ID) return app;
      return { ...app, items: dynamicPlatformItems };
    });
  }, [dynamicPlatformItems]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    if (!q) return applications;

    return applications
      .map(app => ({
        ...app,
        items: app.items.filter(
          item =>
            item.name.toLowerCase().includes(q) ||
            app.name.toLowerCase().includes(q) ||
            (item.parentModule?.toLowerCase().includes(q) ?? false),
        ),
      }))
      .filter(app => app.items.length > 0);
  }, [query, applications]);

  const totalCount = useMemo(
    () => filtered.reduce((sum, app) => sum + app.items.length, 0),
    [filtered],
  );

  const isItemActive = useCallback(
    (item: WaffleItem): boolean =>
      activeModule === item.id || activeSolution === item.id,
    [activeModule, activeSolution],
  );

  return (
    <Modal open={open} onClose={onClose} maxWidth="80vw">
      <div
        data-testid="waffle-menu"
        className="w-full min-h-[60vh] max-h-[85vh] overflow-auto p-0"
      >
        {/* Header Section */}
        <div className="mb-3 flex items-center gap-6">
          <Typography variant="heading" color="primary" component="h2" className="m-0">
            Your solutions
          </Typography>
          <Typography variant="caption" color="secondary" className="m-0">
            jump into any solution you have access to
          </Typography>
        </div>

        {/* Search Bar Section */}
        <div className="mb-8">
          <div className="flex h-10 items-center justify-between gap-2 rounded-8 border-2 border-border-default bg-surface-secondary px-3 py-2.5 transition-all duration-base focus-within:border-border-focus focus-within:bg-brand-primary-soft">
            <SearchBar
              value={query}
              onChange={setQuery}
              placeholder="Search solutions..."
              className="!border-none !bg-transparent !px-0 !py-0 !gap-2 flex-1 [&_input]:!outline-none [&_input]:!border-none [&_input]:!bg-transparent [&_input]:!shadow-none"
            />
            <span className="pointer-events-none whitespace-nowrap text-caption font-semibold uppercase tracking-wide text-text-secondary">
              {totalCount} ACCESSIBLE
            </span>
          </div>
          <div className="mt-6 h-px bg-border-default" />
        </div>

        {/* Empty State */}
        {filtered.length === 0 && (
          <div className="flex items-center justify-center py-13">
            <Typography variant="body2" color="secondary">
              No solutions match &apos;{query}&apos;
            </Typography>
          </div>
        )}

        {/* Categories and Cards Grid */}
        {filtered.length > 0 && (
          <div>
            {filtered.map((app, appIndex) => (
              <div
                key={app.id}
                data-testid={`waffle-menu-category-${app.id}`}
                className={appIndex > 0 ? 'mt-11' : undefined}
              >
                {/* Category Header */}
                <div className="mb-6">
                  <Typography variant="overline" color="secondary" component="h2">
                    {app.name}
                    <span className="ml-3 font-normal opacity-70">
                      {app.items.length}
                    </span>
                  </Typography>
                </div>

                <div className="mb-8 grid grid-cols-3 gap-4">
                  {app.items.map(item => {
                    const active = isItemActive(item);
                    const isSolution = item.type === 'solution';

                    return (
                      <ProductCard
                        key={item.id}
                        title={item.name}
                        icon={resolveLucideIcon(item.icon)}
                        badgeText={item.type.toUpperCase()}
                        isActive={active}
                        className={isSolution ? 'waffle-card-wrapper--solution cursor-pointer' : 'cursor-pointer'}
                        data-testid={`waffle-card-${item.id}`}
                        onClick={() => onSelectItem(item, app.id)}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
